import { useCallback, useEffect, useState } from 'react';
import { LANGUAGE_CONFIG, useTranslation } from '@/lib/i18n';
import IconButton from '../Button/IconButton';
import { HStack } from '../Layout';
import DayInput from './InputDay';
import MonthInput from './InputMonth';
import YearInput from './InputYear';
export interface BirthDate {
  day: string;
  month: string;
  year: string;
}
interface InputBirthDayProps {
  value?: string | BirthDate;
  onChange?: (value: string) => void;
  className?: string;
  isRequired?: boolean;
}
const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ZERO_REGEX = /^0/;
const EMPTY_DATE: BirthDate = {
  day: '',
  month: '',
  year: ''
};

// YYYY-MM-DD形式の日付文字列かどうかを検証
const isValidDateFormat = (dateString: string): boolean => DATE_FORMAT_REGEX.test(dateString);

// 日付文字列をBirthDateオブジェクトに変換
// 先頭の0を除去して扱いやすい形式に変換
const parseDateToValues = (dateString: string | null): BirthDate => {
  if (!dateString || !isValidDateFormat(dateString)) {
    return EMPTY_DATE;
  }
  const [year, month, day] = dateString.split('-');
  return {
    year,
    month: month.replace(ZERO_REGEX, ''),
    day: day.replace(ZERO_REGEX, '')
  };
};

// BirthDateオブジェクトを標準的な日付文字列（YYYY-MM-DD）に変換
const formatDate = (date: BirthDate): string => `${date.year}-${date.month.padStart(2, '0')}-${date.day.padStart(2, '0')}`;
function japaneseDateInputs(value: BirthDate, onChange: (field: keyof BirthDate, value: string) => void) {
  return <>
      <YearInput value={value.year} onChange={v => onChange('year', v)} />
      <MonthInput value={value.month} onChange={v => onChange('month', v)} />
      <DayInput value={value.day} year={value.year} month={value.month} onChange={v => onChange('day', v)} />
    </>;
}
function internationalDateInputs(value: BirthDate, onChange: (field: keyof BirthDate, value: string) => void) {
  return <>
      <DayInput value={value.day} year={value.year} month={value.month} onChange={v => onChange('day', v)} />
      <MonthInput value={value.month} onChange={v => onChange('month', v)} />
      <YearInput value={value.year} onChange={v => onChange('year', v)} />
    </>;
}

// 日付が実在する有効な日付かどうかを検証
// 例：2024-02-31は無効な日付
const isValidDate = (date: BirthDate): boolean => {
  const {
    year,
    month,
    day
  } = date;
  if (!year || !month || !day) return false;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return d.getFullYear() === Number(year) && d.getMonth() === Number(month) - 1 && d.getDate() === Number(day);
};
const InputBirthDay = ({
  value,
  onChange,
  className,
  isRequired
}: InputBirthDayProps) => {
  const initialValue = typeof value === 'string' ? parseDateToValues(value) : value || EMPTY_DATE;
  const [internalValue, setInternalValue] = useState<BirthDate>(initialValue);
  const {
    i18n
  } = useTranslation();
  useEffect(() => {
    const newValue = typeof value === 'string' ? parseDateToValues(value) : value || EMPTY_DATE;

    // oxlint-disable-next-line react/set-state-in-effect -- baseline
    setInternalValue(newValue);
  }, [value]);
  const handleChange = useCallback((field: keyof BirthDate, fieldValue: string) => {
    const newValue = {
      ...internalValue,
      [field]: fieldValue
    };
    setInternalValue(newValue);

    // 部分的な入力の場合はonChangeを呼び出さない
    if (!onChange || !newValue.day || !newValue.month || !newValue.year) {
      return;
    }

    // 有効な日付の場合のみonChangeを呼び出す
    if (isValidDate(newValue)) {
      onChange(formatDate(newValue));
    }
  }, [onChange, internalValue]);
  const handleClearAll = useCallback(() => {
    setInternalValue(EMPTY_DATE);
    if (!onChange) return;
    onChange('');
  }, [onChange]);
  const dateInputs = i18n.language === LANGUAGE_CONFIG['ja-JP'].code ? japaneseDateInputs(internalValue, handleChange) : internationalDateInputs(internalValue, handleChange);
  const hasValue = !!internalValue.year || !!internalValue.month || !!internalValue.day;
  return <HStack className={`min-w-80 items-center space-x-2 ${className}`}>
      {dateInputs}
      {!isRequired && <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" disabled={!hasValue} onClick={handleClearAll} aria-label="Clear date" />}
    </HStack>;
};
export default InputBirthDay;